const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SIMPLE_NUMBER_PATTERN = /^-?\d+(?:\.\d+)?$/;

export const FINIQUITOS_ENGINE_VERSION = '1.0.0';
export const FINIQUITOS_ROUNDING_POLICY = 'half-up-cents';
export const DEFAULT_AGUINALDO_DAYS = 15;
export const DEFAULT_AGUINALDO_BASE_DAYS = 365;
export const DEFAULT_VACATION_PREMIUM_RATE = 0.25;
export const DEFAULT_VACATION_TABLE_VERSION = 'mx-lft-2023-v1';
export const MANUAL_CONCEPT_CATEGORIES = [
  'percepcion',
  'deduccion',
  'prestacion_superior',
  'caso_excepcional'
];
export const CONCEPT_DIRECTIONS = ['credit', 'debit'];

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toComparableNumber(value, fieldName) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new Error(`${fieldName} debe ser un numero finito.`);
    }
    return value;
  }

  if (typeof value === 'string' && SIMPLE_NUMBER_PATTERN.test(value.trim())) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      throw new Error(`${fieldName} debe ser un numero finito.`);
    }
    return parsed;
  }

  throw new Error(`${fieldName} debe ser un numero o una cadena numerica simple.`);
}

function toNonNegativeNumber(value, fieldName) {
  const parsed = toComparableNumber(value, fieldName);
  if (parsed < 0) {
    throw new Error(`${fieldName} no puede ser negativo.`);
  }
  return parsed;
}

function toPositiveInteger(value, fieldName) {
  const parsed = toComparableNumber(value, fieldName);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${fieldName} debe ser un entero mayor que cero.`);
  }
  return parsed;
}

function compareDateParts(left, right) {
  return left.getTime() - right.getTime();
}

function formatDateParts(date) {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0')
  ].join('-');
}

export function parseIsoDate(value, fieldName = 'fecha') {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new Error(`${fieldName} no es una fecha valida.`);
    }
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  }

  if (typeof value !== 'string' || !ISO_DATE_PATTERN.test(value.trim())) {
    throw new Error(`${fieldName} debe tener formato YYYY-MM-DD.`);
  }

  const [yearText, monthText, dayText] = value.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    throw new Error(`${fieldName} no es una fecha valida.`);
  }

  return parsed;
}

export function formatIsoDate(date) {
  return formatDateParts(date);
}

export function roundMoney(value) {
  const numberValue = toComparableNumber(value, 'value');
  const adjusted = numberValue >= 0 ? numberValue + Number.EPSILON : numberValue - Number.EPSILON;
  const rounded = Math.round(adjusted * 100) / 100;
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function roundTo(value, decimals = 10) {
  const numberValue = toComparableNumber(value, 'value');
  const factor = 10 ** decimals;
  const adjusted = numberValue >= 0 ? numberValue + Number.EPSILON : numberValue - Number.EPSILON;
  const rounded = Math.round(adjusted * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function addYearsClamped(date, years) {
  const base = parseIsoDate(date, 'fecha base');
  const increment = toComparableNumber(years, 'years');
  if (!Number.isInteger(increment)) {
    throw new Error('years debe ser un entero.');
  }

  const targetYear = base.getUTCFullYear() + increment;
  const month = base.getUTCMonth();
  const day = base.getUTCDate();
  const candidate = new Date(Date.UTC(targetYear, month, day));

  if (candidate.getUTCMonth() !== month) {
    return new Date(Date.UTC(targetYear, month + 1, 0));
  }

  return candidate;
}

export function daysBetween(startDate, endDate) {
  const start = parseIsoDate(startDate, 'fecha inicial');
  const end = parseIsoDate(endDate, 'fecha final');
  return Math.trunc((end.getTime() - start.getTime()) / MS_PER_DAY);
}

export function daysInclusive(startDate, endDate) {
  return daysBetween(startDate, endDate) + 1;
}

function daysInCalendarYear(year) {
  const start = new Date(Date.UTC(year, 0, 1));
  const end = new Date(Date.UTC(year + 1, 0, 1));
  return daysBetween(start, end);
}

function cloneSerializable(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

function formatMoneyText(value) {
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundMoney(value));
}

function normalizeVacationTableRow(row, index) {
  if (!isPlainObject(row)) {
    throw new Error(`vacationTable.rows[${index}] debe ser un objeto.`);
  }

  const years = toPositiveInteger(row.yearsWorked ?? row.anosTrabajados ?? row.years, `vacationTable.rows[${index}].yearsWorked`);
  const vacationDays = toNonNegativeNumber(
    row.vacationDays ?? row.diasVacaciones ?? row.days,
    `vacationTable.rows[${index}].vacationDays`
  );

  return {
    yearsWorked: years,
    vacationDays: roundMoney(vacationDays)
  };
}

export function buildDefaultVacationTable({ maxYears = 40 } = {}) {
  const yearsMax = toPositiveInteger(maxYears, 'maxYears');
  const rows = [];

  for (let yearsWorked = 1; yearsWorked <= yearsMax; yearsWorked += 1) {
    const vacationDays =
      yearsWorked <= 5
        ? 10 + yearsWorked * 2
        : 20 + 2 * Math.ceil((yearsWorked - 5) / 5);

    rows.push({
      yearsWorked,
      vacationDays
    });
  }

  return rows;
}

export const DEFAULT_VACATION_TABLE = buildDefaultVacationTable();

export function normalizeVacationTable(input = DEFAULT_VACATION_TABLE, version = DEFAULT_VACATION_TABLE_VERSION) {
  const tableInput = Array.isArray(input)
    ? input
    : Array.isArray(input?.rows)
      ? input.rows
      : DEFAULT_VACATION_TABLE;
  const normalizedVersion = String(isPlainObject(input) && input.version ? input.version : version).trim() || DEFAULT_VACATION_TABLE_VERSION;
  const rows = tableInput.map((row, index) => normalizeVacationTableRow(row, index));
  const seenYears = new Set();

  rows.forEach(row => {
    if (seenYears.has(row.yearsWorked)) {
      throw new Error(`vacationTable contiene anos duplicados: ${row.yearsWorked}.`);
    }
    seenYears.add(row.yearsWorked);
  });

  rows.sort((left, right) => left.yearsWorked - right.yearsWorked);

  if (!rows.length || rows[0].yearsWorked !== 1) {
    throw new Error('vacationTable debe incluir el ano 1.');
  }

  return {
    version: normalizedVersion,
    rows
  };
}

export function lookupVacationDays(serviceYearNumber, vacationTable = DEFAULT_VACATION_TABLE) {
  const normalizedServiceYear = toPositiveInteger(serviceYearNumber, 'serviceYearNumber');
  const normalizedTable = Array.isArray(vacationTable)
    ? normalizeVacationTable(vacationTable).rows
    : Array.isArray(vacationTable.rows)
      ? vacationTable.rows
      : normalizeVacationTable(vacationTable).rows;
  let chosenRow = normalizedTable[0];

  for (const row of normalizedTable) {
    if (row.yearsWorked <= normalizedServiceYear) {
      chosenRow = row;
    } else {
      break;
    }
  }

  return {
    serviceYearNumber: normalizedServiceYear,
    yearsWorked: chosenRow.yearsWorked,
    vacationDays: roundMoney(chosenRow.vacationDays)
  };
}

function calculateFullYearsCompleted(startDate, endDate) {
  const start = parseIsoDate(startDate, 'fecha inicial');
  const end = parseIsoDate(endDate, 'fecha final');

  if (compareDateParts(end, start) < 0) {
    throw new Error('La fecha final no puede ser anterior a la fecha inicial.');
  }

  let years = end.getUTCFullYear() - start.getUTCFullYear();

  while (compareDateParts(addYearsClamped(start, years + 1), end) <= 0) {
    years += 1;
  }

  while (years > 0 && compareDateParts(addYearsClamped(start, years), end) > 0) {
    years -= 1;
  }

  return years;
}

export function calculateAntiquity({ startDate, endDate }) {
  const hireDate = parseIsoDate(startDate, 'startDate');
  const separationDate = parseIsoDate(endDate, 'endDate');

  if (compareDateParts(separationDate, hireDate) < 0) {
    throw new Error('endDate no puede ser anterior a startDate.');
  }

  const fullYearsCompleted = calculateFullYearsCompleted(hireDate, separationDate);
  const serviceCycleStart = addYearsClamped(hireDate, fullYearsCompleted);
  const nextServiceCycleStart = addYearsClamped(hireDate, fullYearsCompleted + 1);
  const daysWorkedInCurrentCycle = daysInclusive(serviceCycleStart, separationDate);
  const daysInCurrentCycle = daysBetween(serviceCycleStart, nextServiceCycleStart);

  return {
    startDate: formatIsoDate(hireDate),
    endDate: formatIsoDate(separationDate),
    fullYearsCompleted,
    serviceYearNumber: fullYearsCompleted + 1,
    serviceCycleStart: formatIsoDate(serviceCycleStart),
    nextServiceCycleStart: formatIsoDate(nextServiceCycleStart),
    daysWorkedInCurrentCycle,
    daysInCurrentCycle,
    fractionInCurrentCycle: roundTo(daysWorkedInCurrentCycle / daysInCurrentCycle, 10)
  };
}

export function calculateAguinaldoProportional({
  startDate,
  endDate,
  dailySalary,
  aguinaldoDays = DEFAULT_AGUINALDO_DAYS,
  daysWorkedOverride = null
}) {
  const hireDate = parseIsoDate(startDate, 'startDate');
  const separationDate = parseIsoDate(endDate, 'endDate');
  const salary = toNonNegativeNumber(dailySalary, 'dailySalary');
  const annualDays = toPositiveInteger(aguinaldoDays, 'aguinaldoDays');
  const manualDaysWorked =
    daysWorkedOverride === null || daysWorkedOverride === undefined || daysWorkedOverride === ''
      ? null
      : toNonNegativeNumber(daysWorkedOverride, 'daysWorkedOverride');

  if (compareDateParts(separationDate, hireDate) < 0) {
    throw new Error('endDate no puede ser anterior a startDate.');
  }

  const year = separationDate.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const cycleStart = compareDateParts(hireDate, yearStart) > 0 ? hireDate : yearStart;
  const calculatedDaysWorkedInCalendarYear = daysInclusive(cycleStart, separationDate);
  const daysWorkedInCalendarYear =
    manualDaysWorked === null ? calculatedDaysWorkedInCalendarYear : manualDaysWorked;
  const calendarDaysInYear = DEFAULT_AGUINALDO_BASE_DAYS;
  const rawAmount = salary * annualDays * (daysWorkedInCalendarYear / calendarDaysInYear);

  return {
    annualDays,
    calendarYear: year,
    cycleStart: formatIsoDate(cycleStart),
    cycleEnd: formatIsoDate(separationDate),
    calculatedDaysWorkedInCalendarYear,
    daysWorkedInCalendarYear,
    daysInCalendarYear: calendarDaysInYear,
    excelDaysBase: DEFAULT_AGUINALDO_BASE_DAYS,
    daysWorkedSource: manualDaysWorked === null ? 'date_range' : 'manual',
    fraction: roundTo(daysWorkedInCalendarYear / calendarDaysInYear, 10),
    rawAmount: roundTo(rawAmount, 10),
    amount: roundMoney(rawAmount)
  };
}

export function calculateVacationProportional({
  startDate,
  endDate,
  dailySalary,
  vacationTable = DEFAULT_VACATION_TABLE,
  vacationTableVersion = DEFAULT_VACATION_TABLE_VERSION,
  premiumRate = DEFAULT_VACATION_PREMIUM_RATE
}) {
  const hireDate = parseIsoDate(startDate, 'startDate');
  const separationDate = parseIsoDate(endDate, 'endDate');
  const salary = toNonNegativeNumber(dailySalary, 'dailySalary');
  const premium = toNonNegativeNumber(premiumRate, 'premiumRate');
  const normalizedVacationTable = normalizeVacationTable(vacationTable, vacationTableVersion);

  if (compareDateParts(separationDate, hireDate) < 0) {
    throw new Error('endDate no puede ser anterior a startDate.');
  }

  const totalElapsedDays = daysBetween(hireDate, separationDate);
  const completedYearsTruncated = Math.trunc(totalElapsedDays / 365);
  const daysWorkedInServiceCycle = Math.round(totalElapsedDays - 365 * completedYearsTruncated) + 1;
  const serviceYearNumber = completedYearsTruncated + 1;
  const entitlement = lookupVacationDays(serviceYearNumber, normalizedVacationTable);
  const cycleStart = addYearsClamped(hireDate, completedYearsTruncated);
  const cycleEnd = addYearsClamped(hireDate, completedYearsTruncated + 1);
  const daysInServiceCycle = 365;
  const fraction = daysWorkedInServiceCycle / daysInServiceCycle;
  const daysVacationsProportional = entitlement.vacationDays * fraction;
  const rawVacationAmount = salary * daysVacationsProportional;
  const rawVacationPremiumAmount = rawVacationAmount * premium;

  return {
    completedYearsTruncated,
    nextVacationYear: entitlement.yearsWorked,
    nextVacationServiceYearNumber: serviceYearNumber,
    vacationTableVersion: normalizedVacationTable.version,
    serviceYearNumber,
    vacationDays: entitlement.vacationDays,
    cycleStart: formatIsoDate(cycleStart),
    cycleEnd: formatIsoDate(cycleEnd),
    daysWorkedInServiceCycle,
    daysRemaining: daysWorkedInServiceCycle,
    daysInServiceCycle,
    excelDaysBase: 365,
    fraction: roundTo(fraction, 10),
    daysVacationsProportional: roundTo(daysVacationsProportional, 10),
    rawVacationAmount: roundTo(rawVacationAmount, 10),
    vacationAmount: roundMoney(rawVacationAmount),
    premiumRate: roundTo(premium, 10),
    rawVacationPremiumAmount: roundTo(rawVacationPremiumAmount, 10),
    vacationPremiumAmount: roundMoney(rawVacationPremiumAmount),
    primaAmount: roundMoney(rawVacationPremiumAmount)
  };
}

function normalizeManualConcept(concept, index) {
  if (!isPlainObject(concept)) {
    throw new Error(`concepts[${index}] debe ser un objeto.`);
  }

  const label = String(concept.label || concept.name || '').trim();
  if (!label) {
    throw new Error(`concepts[${index}].label es obligatorio.`);
  }

  const category = String(concept.category || '').trim();
  if (!MANUAL_CONCEPT_CATEGORIES.includes(category)) {
    throw new Error(
      `concepts[${index}].category debe ser uno de: ${MANUAL_CONCEPT_CATEGORIES.join(', ')}.`
    );
  }

  const direction = String(concept.direction || '').trim();
  if (!CONCEPT_DIRECTIONS.includes(direction)) {
    throw new Error(`concepts[${index}].direction debe ser credit o debit.`);
  }

  const amount = roundMoney(toNonNegativeNumber(concept.amount, `concepts[${index}].amount`));

  return {
    id: String(concept.id || `manual-${index + 1}`).trim(),
    label,
    category,
    direction,
    amount,
    note: String(concept.note || '').trim()
  };
}

function buildLineItem({
  id,
  label,
  category,
  direction,
  rawAmount,
  amount,
  source
}) {
  return {
    id,
    label,
    category,
    direction,
    rawAmount: roundTo(rawAmount, 10),
    amount: roundMoney(amount),
    source
  };
}

function normalizeSnapshotConceptKey(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase();
}

function resolveSnapshotConceptIdentity(row = {}) {
  const id = normalizeSnapshotConceptKey(row.id);
  const category = normalizeSnapshotConceptKey(row.category);
  const label = normalizeSnapshotConceptKey(row.concepto || row.label || row.name || row.descripcion);

  if (id === 'aguinaldo proporcional' || category === 'aguinaldo' || label === 'aguinaldo proporcional') {
    return { clave: '23', concepto: 'Aguinaldo (proporcional)' };
  }
  if (id === 'vacaciones proporcionales' || category === 'vacaciones' || label === 'vacaciones proporcionales') {
    return { clave: '21', concepto: 'Vacaciones (proporcional)' };
  }
  if (id === 'prima vacacional' || category === 'prima vacacional' || label === 'prima vacacional') {
    return { clave: '22', concepto: 'Prima Vacacional (proporcional)' };
  }
  if (id === 'vacaciones no pagadas' || category === 'vacaciones no pagadas' || label.includes('vacaciones no pagad')) {
    return { clave: '', concepto: 'Vacaciones (No Pagadas)' };
  }
  if (id === 'prima vacacional no pagada' || category === 'prima vacacional no pagada' || label.includes('prima vacacional no pagad')) {
    return { clave: '', concepto: 'P. Vac. (No Pagada)' };
  }
  if (id === 'isr manual' || category === 'isr' || label === 'isr manual') {
    return { clave: '', concepto: 'ISR retenido' };
  }

  return null;
}

function normalizeSnapshotConceptRow(row = {}) {
  const identity = resolveSnapshotConceptIdentity(row);
  return {
    clave: String(identity?.clave ?? row.clave ?? row.code ?? row.codigo ?? '').trim(),
    concepto: String(identity?.concepto ?? row.concepto ?? row.label ?? row.name ?? row.descripcion ?? '').trim(),
    importe: roundMoney(row.importe ?? row.amount ?? row.monto ?? 0)
  };
}

function normalizeSnapshotVacationRows(rows = []) {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row, index) => {
      const anio = toPositiveInteger(row?.anio ?? row?.yearsWorked ?? row?.years ?? row?.year, `vacacionesConfig.tablaPorAnio[${index}].anio`);
      const dias = toNonNegativeNumber(row?.dias ?? row?.days ?? row?.vacationDays, `vacacionesConfig.tablaPorAnio[${index}].dias`);
      return {
        anio,
        dias: Math.trunc(dias)
      };
    })
    .sort((left, right) => left.anio - right.anio);
}

function normalizeSnapshotEmployer(source = {}) {
  return {
    name: String(
      source.name ||
      source.company ||
      source.label ||
      source.nombre ||
      source.Empresa ||
      source.empresa ||
      source.razonSocial ||
      source.razon_social ||
      source['NOMBRE COMERCIAL'] ||
      ''
    ).trim(),
    rfc: String(source.rfc || source.RFC || source['RFC Patronal'] || '').trim(),
    registroPatronal: String(
      source.registroPatronal ||
        source.patronal ||
        source.reg_patronal ||
        source.regPatronal ||
        source['REG PATRONAL'] ||
        source['Registro Patronal'] ||
        source['Registro patronal'] ||
        ''
    ).trim()
  };
}

function normalizeSnapshotEmployee(source = {}) {
  return {
    fullName: String(source.fullName || source.nombre || source.name || source.employeeName || '').trim(),
    curp: String(source.curp || source.employeeCurp || '').trim()
  };
}

function normalizeSnapshotDates(source = {}) {
  const fallbackDate = source.fechaBaja || source.fechaSeparacion || source.endDate || '';
  const fallbackHire = source.fechaIngreso || source.fechaAlta || source.startDate || '';
  return {
    fechaCalculo: String(source.fechaCalculo || source.calculationDate || fallbackDate || '').trim(),
    fechaIngreso: String(source.fechaIngreso || fallbackHire || '').trim(),
    fechaAlta: String(source.fechaAlta || fallbackHire || '').trim(),
    fechaBaja: String(source.fechaBaja || source.fechaSeparacion || source.endDate || fallbackDate || '').trim(),
    fechaFirma: String(source.fechaFirma || fallbackDate || '').trim(),
    ciudadFirma: String(source.ciudadFirma || source.ciudad || '').trim(),
    estadoFirma: String(source.estadoFirma || source.estado || '').trim()
  };
}

function normalizeSnapshotSalary(source = {}, calculation = {}) {
  const sueldoDiario = roundMoney(
    source.sueldoDiario ?? source.dailySalary ?? calculation?.input?.dailySalary ?? 0
  );
  const sueldoSemanal = roundMoney(
    source.sueldoSemanal ?? source.weeklySalary ?? sueldoDiario * 7
  );

  return {
    sueldoSemanal,
    sueldoDiario
  };
}

function normalizeSnapshotVacationConfig(source = {}, calculation = {}) {
  const calculationVacation = calculation?.vacation || {};
  const tableSource =
    source.tablaPorAnio ||
    source.vacationTable ||
    calculation?.input?.vacationTable?.rows ||
    DEFAULT_VACATION_TABLE;
  const rawPremiumRate =
    source.porcentajePrimaVacacional ??
    source.porcentaje_prima_vacacional ??
    (calculationVacation.premiumRate != null ? calculationVacation.premiumRate * 100 : DEFAULT_VACATION_PREMIUM_RATE * 100);
  const premiumRateNumber = Number(rawPremiumRate);
  const premiumPercent = Number.isFinite(premiumRateNumber)
    ? premiumRateNumber <= 1
      ? premiumRateNumber * 100
      : premiumRateNumber
    : DEFAULT_VACATION_PREMIUM_RATE * 100;

  const normalizedTable = normalizeSnapshotVacationRows(tableSource).length
    ? normalizeSnapshotVacationRows(tableSource)
    : normalizeVacationTable(DEFAULT_VACATION_TABLE, DEFAULT_VACATION_TABLE_VERSION).rows.map(row => ({
        anio: row.yearsWorked,
        dias: Math.trunc(row.vacationDays)
      }));

  return {
    mode: String(source.mode || source.modo || 'legal').trim().toLowerCase() || 'legal',
    tablaPorAnio: normalizedTable,
    porcentajePrimaVacacional: roundTo(premiumPercent, 10),
    allowManualOverride: Boolean(
      source.allowManualOverride ?? source.allow_manual_override ?? false
    ),
    overrideReason: String(source.overrideReason || source.motivoOverride || source.justificacion || '').trim()
  };
}

function normalizeSnapshotRenuncia(source = {}) {
  return {
    aplica: Boolean(source.aplica),
    motivoBaja: String(source.motivoBaja || source.motivo || '').trim()
  };
}

function normalizeSnapshotSignatures(source = {}, employee = {}) {
  return {
    employeeName: String(source.employeeName || source.nombreEmpleado || employee.fullName || '').trim(),
    employeeCurp: String(source.employeeCurp || source.curpEmpleado || employee.curp || '').trim(),
    testigo1Nombre: String(source.testigo1Nombre || source.witness1Name || '').trim(),
    testigo2Nombre: String(source.testigo2Nombre || source.witness2Name || '').trim()
  };
}

export function buildFiniquitosDocumentSnapshot({
  calculation,
  employer = {},
  employee = {},
  dates = {},
  salary = {},
  benefits = {},
  vacationConfig = {},
  renuncia = {},
  signatures = {}
} = {}) {
  if (!calculation) {
    throw new Error('calculation es obligatorio para construir el documentSnapshot.');
  }

  const normalizedEmployer = normalizeSnapshotEmployer(employer);
  const normalizedEmployee = normalizeSnapshotEmployee(employee);
  const normalizedDates = normalizeSnapshotDates(dates);
  const normalizedSalary = normalizeSnapshotSalary(salary, calculation);
  const optionalBenefitNumber = (value, fallback = null) => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return roundTo(value, 10);
  };
  const normalizedBenefits = {
    aguinaldoDiasAnuales: optionalBenefitNumber(
      benefits.aguinaldoDiasAnuales,
      roundTo(calculation?.input?.aguinaldoDays ?? DEFAULT_AGUINALDO_DAYS, 10)
    ),
    aguinaldoDiasLaborados: optionalBenefitNumber(
      benefits.aguinaldoDiasLaborados,
      calculation?.input?.aguinaldoDaysWorkedOverride ?? null
    ),
    diasIndemnizacion: optionalBenefitNumber(
      benefits.diasIndemnizacion,
      roundTo(calculation?.input?.indemnizacionDays ?? 0, 10)
    ),
    vacacionesNoPagadasDias: optionalBenefitNumber(
      benefits.vacacionesNoPagadasDias,
      roundTo(calculation?.input?.unpaidVacationDays ?? 0, 10)
    )
  };
  const normalizedVacationConfig = normalizeSnapshotVacationConfig(vacationConfig, calculation);
  const normalizedRenuncia = normalizeSnapshotRenuncia(renuncia);
  const normalizedSignatures = normalizeSnapshotSignatures(signatures, normalizedEmployee);

  const perceptions = Array.isArray(calculation.lineItems)
    ? calculation.lineItems
        .filter(item => item.direction === 'credit')
        .map(item => normalizeSnapshotConceptRow(item))
    : [];
  const deductions = Array.isArray(calculation.lineItems)
    ? calculation.lineItems
        .filter(item => item.direction === 'debit')
        .map(item => normalizeSnapshotConceptRow(item))
    : [];
  const totals = {
    totalPercepciones: roundMoney(
      calculation?.totals?.grossTotal ?? calculation?.totals?.gross ?? 0
    ),
    totalDeducciones: roundMoney(
      calculation?.totals?.deductionsTotal ?? calculation?.totals?.deductions ?? 0
    ),
    netoPagar: roundMoney(calculation?.totals?.netTotal ?? calculation?.totals?.net ?? 0),
    netoPagarTexto: formatMoneyText(calculation?.totals?.netTotal ?? calculation?.totals?.net ?? 0)
  };

  if (!totals.totalPercepciones) {
    totals.totalPercepciones = roundMoney(perceptions.reduce((sum, row) => sum + row.importe, 0));
  }
  if (!totals.totalDeducciones) {
    totals.totalDeducciones = roundMoney(deductions.reduce((sum, row) => sum + row.importe, 0));
  }
  if (!totals.netoPagar) {
    totals.netoPagar = roundMoney(totals.totalPercepciones - totals.totalDeducciones);
    totals.netoPagarTexto = formatMoneyText(totals.netoPagar);
  }

  return {
    employer: normalizedEmployer,
    employee: normalizedEmployee,
    dates: normalizedDates,
    salary: normalizedSalary,
    benefits: normalizedBenefits,
    vacacionesConfig: normalizedVacationConfig,
    percepciones: perceptions,
    deducciones: deductions,
    totals,
    renuncia: normalizedRenuncia,
    signatures: {
      employeeName: normalizedSignatures.employeeName || normalizedEmployee.fullName,
      employeeCurp: normalizedSignatures.employeeCurp || normalizedEmployee.curp,
      testigo1Nombre: normalizedSignatures.testigo1Nombre,
      testigo2Nombre: normalizedSignatures.testigo2Nombre
    }
  };
}

export function buildFiniquitosSnapshot({
  input,
  antiquity,
  aguinaldo,
  vacation,
  indemnizacion = null,
  unpaidVacation = null,
  manualConcepts,
  manualIsrAmount,
  lineItems,
  totals
}) {
  return {
    engineVersion: FINIQUITOS_ENGINE_VERSION,
    roundingPolicy: FINIQUITOS_ROUNDING_POLICY,
    vacationTableVersion: vacation.vacationTableVersion,
    input,
    antiquity,
    aguinaldo,
    vacation,
    indemnizacion,
    unpaidVacation,
    manualConcepts,
    manualIsrAmount: roundMoney(manualIsrAmount),
    lineItems,
    totals
  };
}

export function calculateFiniquito({
  startDate,
  endDate,
  dailySalary,
  aguinaldoDays = DEFAULT_AGUINALDO_DAYS,
  vacationTable = DEFAULT_VACATION_TABLE,
  vacationTableVersion = DEFAULT_VACATION_TABLE_VERSION,
  vacationPremiumRate = DEFAULT_VACATION_PREMIUM_RATE,
  aguinaldoDaysWorkedOverride = null,
  indemnizacionDays = 0,
  manualIsrAmount = 0,
  unpaidVacationDays = 0,
  concepts = [],
  employee = {},
  employer = {}
} = {}) {
  const hireDate = parseIsoDate(startDate, 'startDate');
  const separationDate = parseIsoDate(endDate, 'endDate');
  const salary = toNonNegativeNumber(dailySalary, 'dailySalary');
  const isr = toNonNegativeNumber(manualIsrAmount, 'manualIsrAmount');
  const normalizedIndemnizacionDays = toNonNegativeNumber(indemnizacionDays, 'indemnizacionDays');
  const normalizedUnpaidVacationDays = toNonNegativeNumber(unpaidVacationDays, 'unpaidVacationDays');
  const normalizedVacationTable = normalizeVacationTable(vacationTable, vacationTableVersion);
  const normalizedConcepts = concepts.map((concept, index) => normalizeManualConcept(concept, index));

  if (compareDateParts(separationDate, hireDate) < 0) {
    throw new Error('endDate no puede ser anterior a startDate.');
  }

  const antiquity = calculateAntiquity({ startDate: hireDate, endDate: separationDate });
  const aguinaldo = calculateAguinaldoProportional({
    startDate: hireDate,
    endDate: separationDate,
    dailySalary: salary,
    aguinaldoDays,
    daysWorkedOverride: aguinaldoDaysWorkedOverride
  });
  const vacation = calculateVacationProportional({
    startDate: hireDate,
    endDate: separationDate,
    dailySalary: salary,
    vacationTable: normalizedVacationTable,
    vacationTableVersion: normalizedVacationTable.version,
    premiumRate: vacationPremiumRate
  });
  const rawUnpaidVacationAmount = salary * normalizedUnpaidVacationDays;
  const rawUnpaidVacationPremiumAmount = rawUnpaidVacationAmount * vacation.premiumRate;
  const unpaidVacation = {
    days: roundTo(normalizedUnpaidVacationDays, 10),
    rawVacationAmount: roundTo(rawUnpaidVacationAmount, 10),
    vacationAmount: roundMoney(rawUnpaidVacationAmount),
    rawVacationPremiumAmount: roundTo(rawUnpaidVacationPremiumAmount, 10),
    vacationPremiumAmount: roundMoney(rawUnpaidVacationPremiumAmount),
    premiumRate: vacation.premiumRate
  };
  const rawIndemnizacionAmount = salary * normalizedIndemnizacionDays;
  const indemnizacion = {
    days: roundTo(normalizedIndemnizacionDays, 10),
    rawAmount: roundTo(rawIndemnizacionAmount, 10),
    amount: roundMoney(rawIndemnizacionAmount)
  };

  const lineItems = [
    buildLineItem({
      id: 'aguinaldo_proporcional',
      label: 'Aguinaldo proporcional',
      category: 'aguinaldo',
      direction: 'credit',
      rawAmount: aguinaldo.rawAmount,
      amount: aguinaldo.amount,
      source: 'statutory'
    }),
    buildLineItem({
      id: 'vacaciones_proporcionales',
      label: 'Vacaciones proporcionales',
      category: 'vacaciones',
      direction: 'credit',
      rawAmount: vacation.rawVacationAmount,
      amount: vacation.vacationAmount,
      source: 'statutory'
    }),
    buildLineItem({
      id: 'prima_vacacional',
      label: 'Prima vacacional',
      category: 'prima_vacacional',
      direction: 'credit',
      rawAmount: vacation.rawVacationPremiumAmount,
      amount: vacation.vacationPremiumAmount,
      source: 'statutory'
    }),
    ...(normalizedUnpaidVacationDays > 0
      ? [
          buildLineItem({
            id: 'vacaciones_no_pagadas',
            label: 'Vacaciones no pagadas',
            category: 'vacaciones_no_pagadas',
            direction: 'credit',
            rawAmount: unpaidVacation.rawVacationAmount,
            amount: unpaidVacation.vacationAmount,
            source: 'manual'
          }),
          buildLineItem({
            id: 'prima_vacacional_no_pagada',
            label: 'Prima vacacional no pagada',
            category: 'prima_vacacional_no_pagada',
            direction: 'credit',
            rawAmount: unpaidVacation.rawVacationPremiumAmount,
            amount: unpaidVacation.vacationPremiumAmount,
            source: 'manual'
          })
        ]
      : []),
    ...(normalizedIndemnizacionDays > 0
      ? [
          buildLineItem({
            id: 'indemnizacion',
            label: 'Indemnización',
            category: 'caso_excepcional',
            direction: 'credit',
            rawAmount: indemnizacion.rawAmount,
            amount: indemnizacion.amount,
            source: 'manual'
          })
        ]
      : []),
    ...normalizedConcepts.map(concept =>
      buildLineItem({
        id: concept.id,
        label: concept.label,
        category: concept.category,
        direction: concept.direction,
        rawAmount: concept.amount,
        amount: concept.amount,
        source: 'manual'
      })
    ),
    buildLineItem({
      id: 'isr_manual',
      label: 'ISR manual',
      category: 'deduccion',
      direction: 'debit',
      rawAmount: isr,
      amount: isr,
      source: 'manual'
    })
  ];

  const grossTotalRaw = lineItems
    .filter(item => item.direction === 'credit')
    .reduce((sum, item) => sum + item.amount, 0);
  const deductionsTotalRaw = lineItems
    .filter(item => item.direction === 'debit')
    .reduce((sum, item) => sum + item.amount, 0);
  const netTotalRaw = grossTotalRaw - deductionsTotalRaw;

  const grossTotal = roundMoney(grossTotalRaw);
  const deductionsTotal = roundMoney(deductionsTotalRaw);
  const netTotal = roundMoney(netTotalRaw);
  const snapshotInput = {
    employee: isPlainObject(employee) ? cloneSerializable(employee) : {},
    employer: isPlainObject(employer) ? cloneSerializable(employer) : {},
    startDate: formatIsoDate(hireDate),
    endDate: formatIsoDate(separationDate),
    dailySalary: roundMoney(salary),
    aguinaldoDays: toPositiveInteger(aguinaldoDays, 'aguinaldoDays'),
    aguinaldoDaysWorkedOverride:
      aguinaldoDaysWorkedOverride === null || aguinaldoDaysWorkedOverride === undefined || aguinaldoDaysWorkedOverride === ''
        ? null
        : roundTo(aguinaldo.daysWorkedInCalendarYear, 10),
    indemnizacionDays: roundTo(normalizedIndemnizacionDays, 10),
    vacationPremiumRate: roundTo(vacationPremiumRate, 10),
    unpaidVacationDays: roundTo(normalizedUnpaidVacationDays, 10),
    manualIsrAmount: roundMoney(isr),
    vacationTable: normalizedVacationTable
  };

  const totals = {
    grossTotal,
    deductionsTotal,
    netTotal
  };

  const result = {
    engineVersion: FINIQUITOS_ENGINE_VERSION,
    roundingPolicy: FINIQUITOS_ROUNDING_POLICY,
    employee: snapshotInput.employee,
    employer: snapshotInput.employer,
    input: snapshotInput,
    antiquity,
    aguinaldo: {
      ...aguinaldo,
      amount: aguinaldo.amount
    },
    vacation: {
      ...vacation,
      vacationAmount: vacation.vacationAmount,
      vacationPremiumAmount: vacation.vacationPremiumAmount
    },
    indemnizacion,
    unpaidVacation,
    manualConcepts: normalizedConcepts,
    manualIsrAmount: roundMoney(isr),
    lineItems,
    totals,
    snapshot: buildFiniquitosSnapshot({
      input: snapshotInput,
      antiquity,
      aguinaldo,
      vacation,
      indemnizacion,
      unpaidVacation,
      manualConcepts: normalizedConcepts,
      manualIsrAmount: isr,
      lineItems,
      totals
    })
  };

  return result;
}
